import visit from 'unist-util-visit'

import { VisitNode } from './types'

import type { Parent, Data } from 'unist'

type IgnoreNonOneOrderedListsPluginProps = {
    enabled: boolean
    markdown: string
}

type ListVisitNode = VisitNode & {
    ordered?: boolean
    start?: number
    children?: ListItemVisitNode[]
}

type ListItemVisitNode = VisitNode & {
    children?: ParentVisitNode[]
}

type ParentVisitNode = VisitNode & {
    children?: VisitNode[]
}

const getNodeSource = (markdown: string, node: VisitNode) => {
    const startOffset = node.position?.start.offset
    const endOffset = node.position?.end.offset

    if (typeof startOffset !== 'number' || typeof endOffset !== 'number') {
        return undefined
    }

    return markdown.slice(startOffset, endOffset)
}

const getListItemMarker = (markdown: string, node: VisitNode) => {
    const source = getNodeSource(markdown, node)
    const match = source?.match(/^\s*(\d+[.)])\s+/)

    return match?.[1]
}

const getListItemContent = (node: ListItemVisitNode) => {
    const paragraph = node.children?.find((child) => child.type === 'paragraph')

    return paragraph?.children
}

const isVisitNode = (node: VisitNode | undefined): node is VisitNode => !!node

export const ignoreNonOneOrderedLists = ({ enabled, markdown }: IgnoreNonOneOrderedListsPluginProps) => {
    if (!enabled) {
        return () => {}
    }

    return (tree: VisitNode) => {
        visit(tree, 'list', (node: ListVisitNode, index: number, parent: Parent<VisitNode, Data> | undefined) => {
            if (
                !parent ||
                typeof index !== 'number' ||
                !node.ordered ||
                typeof node.start !== 'number' ||
                node.start === 1
            ) {
                return
            }

            const source = getNodeSource(markdown, node)
            if (!source || !node.children) {
                return
            }

            const children: VisitNode[] = node.children.flatMap((child, childIndex): VisitNode[] => {
                // @ts-expect-error should be fine I guess
                const marker = getListItemMarker(markdown, child) || `${node.start + childIndex}.`
                const content = getListItemContent(child)

                if (!content) {
                    // @ts-expect-error should be fine I guess
                    return [
                        childIndex ? { type: 'text', value: '\n' } : undefined,
                        {
                            type: 'text',
                            value: getNodeSource(markdown, child) || '',
                        },
                    ].filter(isVisitNode)
                }

                return [
                    childIndex ? { type: 'text', value: '\n' } : undefined,
                    {
                        type: 'text',
                        value: `${marker} `,
                    },
                    ...content,
                ].filter(isVisitNode)
            })

            parent.children.splice(index, 1, {
                type: 'paragraph',
                // @ts-expect-error should be fine I guess
                children,
            })
        })
    }
}
